import { useState } from 'react';
import { currencySymbol } from '../flight_format.js';

/**
 * Campo de valor da régua de preço — "R$ 1.153" editável. Sincronizado nos DOIS
 * sentidos (#2110): o valor vindo do estado repõe o rascunho (padrão de
 * derived-state do React), e digitar um número dentro da faixa VIVA
 * (`floor`..`ceiling`, a outra ponta inclusa) corta em vivo; fora dela o commit
 * espera o blur — capar tecla a tecla brigaria com quem ainda está digitando. No
 * blur o valor é capado ao `limit` do CONJUNTO, não à outra ponta: cruzar a
 * outra ponta é decisão de quem digitou, e quem resolve o cruzamento é o
 * `onCommit` da seção (#3239 C5).
 *
 * O rascunho guarda só dígitos; o que se vê leva o separador de milhar do
 * locale, o mesmo dos preços dos cards ("R$ 1.222").
 */
export function PriceField({
  label,
  value,
  floor,
  ceiling,
  limit,
  currency,
  locale,
  onCommit,
}: Readonly<{
  label: string;
  /** O locale do separador de milhar ("1.500" em pt-BR). */
  locale: string;
  value: number;
  floor: number;
  ceiling: number;
  limit: { min: number; max: number };
  currency: string;
  onCommit: (value: number) => void;
}>) {
  const [prev, setPrev] = useState(value);
  const [draft, setDraft] = useState(String(value));
  if (prev !== value) {
    setPrev(value);
    setDraft(String(value));
  }
  return (
    <label className="ffp-pricefield">
      <span className="ffp-pricefield-cur" aria-hidden="true">
        {currencySymbol(currency)}
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={groupDigits(draft, locale)}
        aria-label={label}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D+/g, '');
          setDraft(digits);
          const parsed = Number(digits);
          if (digits !== '' && parsed >= floor && parsed <= ceiling) onCommit(parsed);
        }}
        onBlur={() => {
          const parsed = Number(draft);
          if (draft === '' || Number.isNaN(parsed)) {
            setDraft(String(value));
            return;
          }
          const clamped = Math.min(Math.max(parsed, limit.min), limit.max);
          setDraft(String(clamped));
          if (clamped !== value) onCommit(clamped);
        }}
      />
    </label>
  );
}

/** Dígitos crus → número com separador de milhar do locale ("1500" → "1.500" em
 *  pt-BR). Vazio fica vazio: é o campo sendo apagado, não um zero. */
function groupDigits(digits: string, locale: string): string {
  if (digits === '') return '';
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(digits));
  } catch {
    return digits;
  }
}
